import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { getLimitsForTier, freeTierLimits } from '../lib/stripe.js';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user, isLoaded: isUserLoaded } = useUser();
  const { getToken } = useAuth();
  const [subscription, setSubscription] = useState({
    tier: 'free',
    status: null,
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
    isLoading: true,
  });

  // Get subscription info from Clerk user metadata
  const fetchSubscription = useCallback(async () => {
    if (!isUserLoaded) return;

    if (!user) {
      setSubscription({
        tier: 'free',
        status: null,
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false,
        isLoading: false,
      });
      return;
    }

    try {
      // Subscription info is stored in Clerk public metadata
      const publicMetadata = user.publicMetadata || {};
      let isAdmin = publicMetadata.isAdmin === true;
      try {
        const token = await getToken();
        const response = await fetch('/api/admin?view=me', { headers: { Authorization: `Bearer ${token}` } });
        if (response.ok) isAdmin = Boolean((await response.json()).isAdmin);
      } catch (adminError) {
        console.warn('Admin status could not be verified:', adminError.message);
      }
      const tier = publicMetadata.subscriptionTier || 'free';
      const status = publicMetadata.subscriptionStatus || null;
      const currentPeriodEnd = publicMetadata.subscriptionCurrentPeriodEnd || null;
      const cancelAtPeriodEnd = publicMetadata.subscriptionCancelAtPeriodEnd || false;

      setSubscription({
        tier,
        status,
        currentPeriodEnd: currentPeriodEnd ? new Date(currentPeriodEnd) : null,
        cancelAtPeriodEnd,
        isAdmin,
        tier: isAdmin ? 'admin' : tier,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription(prev => ({ ...prev, isLoading: false }));
    }
  }, [user, isUserLoaded, getToken]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Get the limits for the current tier
  const limits = subscription.isAdmin
    ? { storiesPerMonth: Infinity, maxCharacters: Infinity, regenerationsPerStory: Infinity, audioEnabled: true }
    : getLimitsForTier(subscription.tier);

  // Check if user has an active subscription
  const hasActiveSubscription = subscription.status === 'active' || subscription.status === 'trialing';

  // Check if user can perform an action based on their tier
  const canCreateStory = useCallback((currentMonthlyCount) => {
    return currentMonthlyCount < limits.storiesPerMonth;
  }, [limits.storiesPerMonth]);

  const canCreateCharacter = useCallback((currentCharacterCount) => {
    return currentCharacterCount < limits.maxCharacters;
  }, [limits.maxCharacters]);

  const canRegenerateStory = useCallback((currentRegenerations) => {
    return currentRegenerations < limits.regenerationsPerStory;
  }, [limits.regenerationsPerStory]);

  const canUseAudio = limits.audioEnabled;

  const value = {
    ...subscription,
    limits,
    hasActiveSubscription,
    canCreateStory,
    canCreateCharacter,
    canRegenerateStory,
    canUseAudio,
    refreshSubscription: fetchSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
